import React, { useMemo, useCallback } from "react";
import { Collapse, Typography } from "@mui/material";
import { darken } from "polished";
import { NavLink } from "react-router-dom";
import styled from "styled-components";
import SidebarCategory from "./SidebarCategory";
import SidebarLink from "./SidebarLink";
import routes, { PATHS } from "../../routes";
import useGlobalStore from "src/store/useGlobalStore";
import { JOB_TYPES } from "src/storage/constants";

const SidebarSection = styled(Typography)`
  color: ${(props) => props.theme.sidebar.color};
  padding: ${(props) => props.theme.spacing(2)}px
    ${(props) => props.theme.spacing(7)}px ${(props) => props.theme.spacing(2)};
  opacity: 0.9;
  display: block;

  &:hover {
    background-color: ${(props) =>
      darken(0.05, props.theme.sidebar.background)};
  }
`;

const SidebarItems = () => {
  const config = useGlobalStore((state) => state.config);
  const isRetail = useGlobalStore((state) => state.isRetail);
  const { user, current_company: company } = config;
  const isManager = user?.job === JOB_TYPES.SUPERVISOR;

  const getSidebarRoutes = useCallback(
    (allRoutes) => {
      const allSidebarRoutes = allRoutes?.filter((r) => r.inSidebar);
      const canAccessOrgRep = user?.can_access_organization_report;

      if (isManager && isRetail) {
        const filtered = allRoutes.filter((route) => route.allowManager);

        return user.can_deploy_vendor_content
          ? filtered
          : filtered.filter((route) => route.path !== PATHS.MARKETPLACE);
      }
      if (isRetail) {
        if (canAccessOrgRep)
          return allSidebarRoutes.filter((route) => route.allowRetail);
        return allSidebarRoutes.filter(
          (route) =>
            route.allowRetail && route.path !== PATHS.ORGANIZATION_REPORT,
        );
      }
      return allSidebarRoutes.filter((route) => route.allowVendor);
    },
    [
      isManager,
      isRetail,
      user.can_deploy_vendor_content,
      user.can_access_organization_report,
    ],
  );

  const addBadgesToRoutes = useCallback(
    (routes) => {
      const { pending_access_requests } = company || {};
      const { team_members, admins, managers } = pending_access_requests || {};
      return routes.map((r) => ({
        ...r,
        badge:
          r.id === "Admins" && admins > 0
            ? admins
            : r.id === "Supervisors" && managers > 0
              ? managers
              : r.id === "Team Members" && team_members > 0
                ? team_members
                : null,
      }));
    },
    [company],
  );

  const sidebarRoutes = useMemo(
    () => getSidebarRoutes(routes),
    [getSidebarRoutes, routes],
  );
  const sidebarRoutesWithBadges = useMemo(
    () => addBadgesToRoutes(sidebarRoutes),
    [addBadgesToRoutes, sidebarRoutes],
  );

  return (
    <>
      {sidebarRoutesWithBadges.map((category, index) => (
        <React.Fragment key={category.id || index}>
          {category.header ? (
            <SidebarSection>{category.header}</SidebarSection>
          ) : null}
          {category.children && category.icon ? (
            <>
              <SidebarCategory
                isOpen={!openRoutes[index]}
                isCollapsable
                name={category.id}
                icon={category.icon}
                button
                onClick={() => toggle(index)}
              />
              <Collapse in={openRoutes[index]} timeout="auto" unmountOnExit>
                {category.children.map((route, idx) => (
                  <SidebarLink
                    key={route.id || idx}
                    name={route.name}
                    to={route.path}
                    icon={route.icon}
                    badge={route.badge}
                  />
                ))}
              </Collapse>
            </>
          ) : category.icon ? (
            <SidebarCategory
              isCollapsable={false}
              name={category.id}
              to={category.path}
              activeClassName="active"
              component={NavLink}
              icon={category.icon}
              exact
              button
              badge={category.badge}
            />
          ) : null}
        </React.Fragment>
      ))}
    </>
  );
};

export default SidebarItems;
